import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith, take, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { OrganisationConfiguration } from 'src/app/global/global.model';
import { State } from 'src/app/store';
import { ConfirmDialogComponent } from 'src/app/global/components/dialogs/confirm-dialog/confirm-dialog.component';
import { organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { CardConfiguration, Permissions } from '@bciti/types';
import { Residence } from 'src/app/residence/residence.model';
import { ReviewResidenceDialogComponent, ReviewResidenceDialogData } from 'src/app/residence/dialogs/review-residence-dialog/review-residence-dialog.component';
import { UserService } from '../../service/user.abstract.service';
import { SearchUsersAction } from '../../store/user.actions';
import { CardActions, CardStatus, CardType, UserCard, UserRecordTransformedUser } from '../../user.model';
import { UserCardRequestDetailDialogComponent } from '../../dialogs/user-card-request-detail-dialog/user-card-request-detail-dialog.component';
import { UserCardRequestFormData } from '../../forms/user-card-request-detail-form/user-card-request-detail-form.component';
import { UserCardDeleteDialogComponent } from '../../dialogs/user-card-delete-dialog/user-card-delete-dialog.component';
import { UserConfirmAccountDialogComponent } from '../../dialogs/user-confirm-account-dialog/user-confirm-account.component';
import { UserDeleteDialogComponent } from '../../dialogs/user-delete-dialog/user-delete.component';
import { UserFamilyAndCardDialogComponent } from '../../dialogs/user-family-and-card-dialog/user-family-and-card-dialog.component';
import { UserRoleModalComponent } from '../user-role/user-role-modal.component';
import { UserCardNotConnectedRequestDetailDialogComponent } from '../../dialogs/user-card-not-connected-request-detail-dialog/user-card-not-connected-request-detail-dialog.component';
import { UserCardRequestApprovalNotNeededDialogComponent } from '../../dialogs/user-card-request-approval-not-needed-dialog/user-card-request-approval-not-needed-dialog.component';
import { authPermissionsSelector } from '../../../auth/store/auth.selectors';

@Component({
    selector: 'app-user-table',
    templateUrl: './user-table.component.html',
    styleUrls: ['./user-table.component.scss'],
})
export class UserTableComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    public CardStatus = CardStatus;

    public CardType = CardType;

    public CardAction = CardActions;

    public isConnected = true;

    public autoApprove = false;

    public displayModifyAccessControl = false;

    public displayViewCardRequest = false;

    public displayViewFamilyRequest = false;

    public displayModifyUser = false;

    orgConfig: OrganisationConfiguration;

    orgTag: string;

    public screenSize: number;

    public cardConfig: CardConfiguration[] = [];

    private approvedCardRequests = new Set<string>();

    @Input() public length;

    @Input() public pageSize;

    @Input() public pageIndex;

    @Input() public exportData = false;

    @Input() public maxExportResults = 0;

    @Input() public data: UserRecordTransformedUser[] = [];

    @Input() public columns = [];

    @Input() public isLoading = false;

    @Input() public residenceMap = new Map<string, Residence[]>();

    // Generate skeleton rows for loading state
    public skeletonRows = Array(10).fill({});

    @Output() public page: EventEmitter<PageEvent> = new EventEmitter();

    @Output() public export: EventEmitter<PageEvent> = new EventEmitter();

    @Output() public approvalCompleted: EventEmitter<void> = new EventEmitter();

    get displayedColumns(): string[] {
        return this.columns.map((c) => c?.key);
    }

    constructor(
        public dialog: MatDialog,
        private chRef: ChangeDetectorRef,
        private userService: UserService,
        private store: Store<State>,
        private messageService: MessageService,
        public translate: TranslateService,
    ) {}

    public ngOnInit(): void {
        if (!this.pageSize) {
            this.pageSize = 10;
        }
        if (!this.pageIndex) {
            this.pageIndex = 0;
        }
        if (!this.length) {
            this.length = 0;
        }

        fromEvent(window, 'resize')
            .pipe(
                startWith(() => this.recordScreenSize()),
                takeUntil(this.destroy$),
                throttleTime(500),
                map(() => this.recordScreenSize()),
            )
            .subscribe();

        this.store
            .select(organisationConfigurationSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((config) => {
                    this.orgConfig = config;
                    this.cardConfig = config?.support?.cards || [];
                }),
            )
            .subscribe();

        this.store
            .select(organisationTagSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((tag) => {
                    this.orgTag = tag;
                }),
            )
            .subscribe();

        this.store
            .select(authPermissionsSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((permissions) => {
                    if (permissions?.includes(Permissions.ACCESS_CONTROL)) {
                        this.displayModifyAccessControl = true;
                    }

                    if (permissions?.includes(Permissions.CARD)) {
                        this.displayViewCardRequest = true;
                    }

                    if (permissions?.includes(Permissions.FAMILY)) {
                        this.displayViewFamilyRequest = true;
                    }

                    if (permissions?.includes(Permissions.PROFILE)) {
                        this.displayModifyUser = true;
                    }
                }),
            )
            .subscribe();

        this.chRef.detectChanges();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public pageChange($event: any): void {
        this.page.emit($event);
    }

    openDeleteUserDialog(userId: string): void {
        this.dialog
            .open(UserDeleteDialogComponent, {
                data: {
                    id: userId,
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                mergeMap(() => this.userService.deleteUser(userId)),
                tap(() => this.store.dispatch(SearchUsersAction())),
            )
            .subscribe();
    }

    openConfirmAccountDialog(userId: string, email: string): void {
        this.dialog
            .open(UserConfirmAccountDialogComponent, {
                data: {
                    id: userId,
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                mergeMap(() => this.userService.sendConfirmAccountEmail(email)),
            )
            .subscribe();
    }

    openUserRoleDialog(element): void {
        const dialogRef = this.dialog.open(UserRoleModalComponent, {
            data: { user: element },
        });
        dialogRef.componentInstance.user = element;
    }

    isCardRequestApproved(element, card): boolean {
        const requestId = `${element.id}-${card._id}`;
        return this.approvedCardRequests.has(requestId);
    }

    onCardRequestApproved(event: { userId: string; cardId: string }): void {
        const requestId = `${event.userId}-${event.cardId}`;
        this.approvedCardRequests.add(requestId);
    }

    openCardRequestDetailDialog(element, card, action: CardActions): void {
        const cardConfig = this.cardConfig.find((c) => c.type === card.cardType);
        this.isConnected = cardConfig?.isConnected;
        this.autoApprove = cardConfig?.autoApprove;

        if (this.isConnected == false && card.cardType == CardType.LIBRARY) {
            this.dialog
                .open(UserCardNotConnectedRequestDetailDialogComponent, { data: { user: element, card, action } })
                .afterClosed()
                .pipe(
                    filter((result) => !!result),
                    tap((result) => {
                        this.store.dispatch(SearchUsersAction());
                        // Notify parent to refresh pending counts
                        this.approvalCompleted.emit();
                        // Check for family dialog chaining
                        this.handleCardDialogChaining(result);
                    }),
                )
                .subscribe();
        } else if (
            this.isConnected != false &&
            card.status == CardStatus.PROCESSING &&
            this.autoApprove == true &&
            card.actionType == CardActions.LINK &&
            !(card.cardType === CardType.LIBRARY && this.orgTag === 'STECATHERINE')
        ) {
            this.dialog
                .open(UserCardRequestApprovalNotNeededDialogComponent)
                .afterClosed()
                .pipe(
                    filter((result) => !!result),
                    tap(() => this.store.dispatch(SearchUsersAction())),
                )
                .subscribe();
        } else if (element.libraryCard?.renewal?.number) {
            const dialogRef = this.dialog.open(UserCardRequestDetailDialogComponent, { data: { user: element, card, action: CardActions.RENEW } });
            dialogRef.componentInstance.approvalPressed.subscribe((event) => this.onCardRequestApproved(event));
            dialogRef
                .afterClosed()
                .pipe(
                    filter((result) => !!result),
                    tap((result) => {
                        this.store.dispatch(SearchUsersAction());
                        // Notify parent to refresh pending counts
                        this.approvalCompleted.emit();
                        // Check for family dialog chaining
                        this.handleCardDialogChaining(result);
                    }),
                )
                .subscribe();
        } else {
            const dialogRef = this.dialog.open(UserCardRequestDetailDialogComponent, { data: { user: element, card, action } });
            dialogRef.componentInstance.approvalPressed.subscribe((event) => this.onCardRequestApproved(event));
            dialogRef
                .afterClosed()
                .pipe(
                    filter((result) => !!result),
                    tap((result) => {
                        this.store.dispatch(SearchUsersAction());
                        // Notify parent to refresh pending counts
                        this.approvalCompleted.emit();
                        // Check for family dialog chaining
                        this.handleCardDialogChaining(result);
                    }),
                )
                .subscribe();
        }
    }

    openCardDeleteDialog(element, card): void {
        this.dialog
            .open(UserCardDeleteDialogComponent, { data: { user: element, card } })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap(() => this.store.dispatch(SearchUsersAction())),
            )
            .subscribe();
    }

    openResetPasswordDialog(element): void {
        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
            width: '600px',
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('pages.userRecords.resetPassword'),
                body: this.translate.instant('pages.userRecords.resetPasswordConfirmationText'),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.yes'),
                        class: 'bciti-button  bciti-button--blue',
                    },
                    cancel: {
                        text: this.translate.instant('common.no'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        confirmDialog.componentInstance.onSubmit.subscribe((confirm) => {
            confirmDialog.componentInstance.dialogRef.close();
            if (confirm && element.email?.includes('@')) {
                this.userService
                    .sendForgotPasswordEmail(element.email)
                    .pipe(
                        take(1),
                        tap(() => this.messageService.success(this.translate.instant('pages.userRecords.resetPasswordSuccess'))),
                        catchError((error) => {
                            this.messageService.error(this.translate.instant('pages.userRecords.resetPasswordError'));
                            return of(undefined);
                        }),
                    )
                    .subscribe();
            }
        });
    }

    openFamilyAndCardDialog(element: UserRecordTransformedUser): void {
        this.dialog
            .open(UserFamilyAndCardDialogComponent, {
                data: { user: element },
                panelClass: 'familyAndCardDialog',
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap((result) => {
                    this.store.dispatch(SearchUsersAction());
                    // Notify parent to refresh pending counts
                    this.approvalCompleted.emit();
                    // Check for card dialog chaining (when User A has pending card request after family approval)
                    this.handleFamilyDialogChaining(result);
                }),
            )
            .subscribe();
    }

    /**
     * Handle chaining from card dialog to family dialog
     * If card was approved and user has pending family requests, show clickable toast
     */
    private handleCardDialogChaining(result: boolean | { success: boolean; openFamilyDialogForUser?: UserRecordTransformedUser }): void {
        if (typeof result === 'object' && result.openFamilyDialogForUser) {
            const user = result.openFamilyDialogForUser;
            // Show clickable toast for pending family requests
            const toast = this.messageService.infoClickable(
                this.translate.instant('pages.userRecords.card.requestDetailsDialog.message.pendingFamilyRequest.message', {
                    firstName: user.firstName,
                    lastName: user.lastName,
                }),
                this.translate.instant('pages.userRecords.card.requestDetailsDialog.message.pendingFamilyRequest.title'),
            );
            // Open family dialog when toast is clicked
            toast.onTap.subscribe(() => {
                this.openFamilyAndCardDialog(user);
            });
        }
    }

    /**
     * Handle chaining from family dialog to card dialog
     * If family was approved and User A has pending card request, show clickable toast
     */
    private handleFamilyDialogChaining(result: boolean | { success: boolean; openCardDialogForUserA?: UserCardRequestFormData }): void {
        if (typeof result === 'object' && result.openCardDialogForUserA) {
            const cardData = result.openCardDialogForUserA;
            // Show clickable toast for pending card request
            const toast = this.messageService.infoClickable(
                this.translate.instant('pages.userFamily.messages.pendingCardRequest.message', {
                    firstName: cardData.user.firstName,
                    lastName: cardData.user.lastName,
                }),
                this.translate.instant('pages.userFamily.messages.pendingCardRequest.title'),
            );
            // Open card dialog when toast is clicked
            toast.onTap.subscribe(() => {
                this.openCardRequestDetailDialog(cardData.user, cardData.card, cardData.action);
            });
        }
    }

    private recordScreenSize(): void {
        this.screenSize = document.body.offsetWidth;
    }

    public exportDataTable(): void {
        this.export.emit();
    }

    getCardIcon(cardType: string): string {
        const cardConfig = this.cardConfig.find((c) => c.type === cardType);
        return cardConfig?.identity?.icon || 'icon-my-card';
    }

    getCardLabel(cardType: string): string {
        const cardConfig = this.cardConfig.find((c) => c.type === cardType);
        const configLabel = cardConfig?.identity?.label?.[this.translate.currentLang];
        if (configLabel) {
            return configLabel;
        }
        // Fallback to i18n translation if config label is not available
        return this.translate.instant(`pages.userRecords.cardTypes.${cardType}`);
    }

    showModifyUser(element: UserRecordTransformedUser): boolean {
        if (this.displayModifyUser === false) {
            return false;
        }
        return element.email?.includes('@');
    }

    // User has a card request as 'PROCESSING'. A pending family link doesn't block it:
    // approving the card chains to the family approval dialog.
    showCardRequest(cardStatus: CardStatus): boolean {
        if (this.displayViewCardRequest === false) {
            return false;
        }

        return cardStatus === CardStatus.PROCESSING;
    }

    showCardRenewalRequest(elem): boolean {
        if (this.displayViewCardRequest === false) {
            return false;
        }

        return !!elem;
    }

    getFamilyLinksCount(element: UserRecordTransformedUser): number {
        if (!element.userFamily?.family) {
            return 0;
        }
        const links = (element.userFamily.family as any)?.links || element.userFamily.family;
        if (Array.isArray(links)) {
            return links.filter((link) => link.cityApproved && link.familyApproved).length;
        }
        return 0;
    }

    hasPendingFamilyApproval(element: UserRecordTransformedUser): boolean {
        if (!element.userFamily?.family) {
            return false;
        }
        const links = (element.userFamily.family as any)?.links || element.userFamily.family;
        if (Array.isArray(links)) {
            return links.some((link) => link.familyApproved && !link.cityApproved);
        }
        return false;
    }

    /**
     * Check if a card has a pending renewal request
     */
    hasPendingRenewal(card: UserCard): boolean {
        return !!card?.renewal?._id;
    }

    /**
     * Check if a card's renewal has been approved and is syncing with the external connector
     */
    isRenewalSyncing(card: UserCard): boolean {
        return card?.renewal?.status === CardStatus.ACTIVATING;
    }

    /**
     * Check if a card should show its status text (e.g., "Expired")
     * Returns true only if status is not ACTIVE, not PROCESSING, and no pending renewal
     */
    shouldShowCardStatus(card: UserCard): boolean {
        if (!card) return false;
        return card.status !== undefined && card.status !== CardStatus.ACTIVE && card.status !== CardStatus.PROCESSING && !this.hasPendingRenewal(card);
    }

    /**
     * Check if a card with PROCESSING status should be clickable for approval
     */
    isProcessingCardClickable(card: UserCard, element: UserRecordTransformedUser): boolean {
        return this.displayViewCardRequest && this.showCardRequest(card.status as CardStatus) && !this.isCardRequestApproved(element, card);
    }

    /**
     * Check if a card with pending renewal should be clickable.
     * A card already syncing with the external connector (isRenewalSyncing) must not be
     * re-clickable to manually approve again, same as an ACTIVATING ("en création") card.
     */
    isRenewalCardClickable(card: UserCard): boolean {
        return this.displayViewCardRequest && !this.isRenewalSyncing(card);
    }

    getResidences(element: UserRecordTransformedUser): Residence[] {
        return this.residenceMap.get(element.id) || [];
    }

    getPendingResidences(element: UserRecordTransformedUser): Residence[] {
        return this.getResidences(element).filter((r) => r.verification?.status === 'PENDING' || r.renewal?.status === 'PENDING');
    }

    hasPendingResidences(element: UserRecordTransformedUser): boolean {
        return this.getPendingResidences(element).length > 0;
    }

    openResidenceReviewDialog(element: UserRecordTransformedUser): void {
        const pendingResidences = this.getPendingResidences(element);
        if (pendingResidences.length === 0) return;

        const residence = pendingResidences[0];
        const dialogData: ReviewResidenceDialogData = {
            residence,
            citizen: {
                id: element.id,
                firstName: element.firstName,
                lastName: element.lastName,
                email: element.email,
                phone: element.phone,
                picture: element.picture,
            },
        };

        this.dialog
            .open(ReviewResidenceDialogComponent, {
                width: '560px',
                data: dialogData,
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap(() => {
                    this.store.dispatch(SearchUsersAction());
                    this.approvalCompleted.emit();
                }),
            )
            .subscribe();
    }

    /**
     * Get all cards that can be deleted (ACTIVE or EXPIRED status)
     * Includes bcitiCard, legacyCard, and citizenCard (identity verification cards)
     */
    getDeletableCards(element: UserRecordTransformedUser): UserCard[] {
        const deletableCards: UserCard[] = [];
        const cardTypes = ['bcitiCard', 'legacyCard', 'citizenCard', 'sportsCard', 'libraryCard'] as const;

        for (const cardType of cardTypes) {
            const card = element[cardType];
            if (card && (card.status === CardStatus.ACTIVE || card.status === CardStatus.EXPIRED)) {
                deletableCards.push(card);
            }
        }

        return deletableCards;
    }
}
